import React, {
  useState,
  Suspense,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Layout } from "antd";
import { renderRoutes } from "utils/route";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar/SidebarContainer";
import styled, { keyframes } from "styled-components";
import { useTheme } from "themes/useTheme";
import { appConfigService } from "services/appConfigService";
import useSizeMediaQuery from "hooks/useSizeMediaQuery";
import SidebarMobile from "components/Sidebar/SidebarContainer.mobile";
import Chat from "components/chat";
import { isMicroApp } from "utils/microApp";

const { Content } = Layout;

interface IProps {
  route?: any;
}

const PrivateLayout = (props: IProps) => {
  const {
    route: { routes },
  } = props;

  const frameId = useRef<null | number>(null);

  useEffect(() => {
    return () => {
      if (frameId.current) {
        cancelAnimationFrame(frameId.current);
      }
    };
  }, []);

  const { themeVariables } = useTheme();

  const size = useSizeMediaQuery();

  const [collapsed, setCollapsed] = useState(
    appConfigService.getItem("collapsed")
  );

  const [drawerVisible, setDrawerVisible] = useState(false);

  const handleCollapsedChange = useCallback(() => {
    // frameId.current = requestAnimationFrame(() => {
    setCollapsed((collapsed) => {
      appConfigService.setItem("collapsed", !collapsed);
      return !collapsed;
    });
    // });
  }, [setCollapsed]);

  const handleDrawerChange = useCallback(() => {
    frameId.current = requestAnimationFrame(() => {
      setDrawerVisible((drawerVisible) => !drawerVisible);
    });
  }, [setDrawerVisible]);

  const isMobile = useMemo(() => ["xs", "sm"].includes(size), [size]);

  const handleToggleClick = isMobile
    ? handleDrawerChange
    : handleCollapsedChange;

  const handleCloseDrawer = useCallback(() => {
    setDrawerVisible(false);
  }, [setDrawerVisible]);

  return (
    <StyledLayout>
      {!isMicroApp && (
        <>
          {isMobile ? (
            <SidebarMobile
              routes={routes}
              drawerVisible={drawerVisible}
              onDrawerClose={handleCloseDrawer}
            />
          ) : (
            <Sidebar routes={routes} collapsed={collapsed} />
          )}
        </>
      )}
      <StyledSubLayout
        id="content"
        style={{
          width: isMicroApp
            ? "100%"
            : !isMobile
            ? `calc(100vw - ${collapsed ? "80px" : "230px"})`
            : "100vw",
          maxHeight: isMicroApp ? "unset" : undefined,
          overflow: isMicroApp ? "unset" : undefined,
        }}
      >
        {!isMicroApp && (
          <Navbar collapsed={collapsed} onToggle={handleToggleClick} />
        )}
        <Content
          style={{
            backgroundColor: themeVariables.base_color,
          }}
        >
          {renderRoutes(routes)}
        </Content>
        <Chat />
      </StyledSubLayout>
    </StyledLayout>
  );
};

export default React.memo(PrivateLayout);

const fadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const StyledLayout = styled(Layout)`
  animation: ${fadeIn} 0.1s linear;
  transition: visibility 0.1s linear;
  height: 100vh;
  overflow-y: scroll;
  /* * {
    transition: background-color 0.4s ease 0s !important;
  } */
  overscroll-behavior-x: none;
  &::-webkit-scrollbar {
    width: 0px !important;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none;
  padding-bottom: 8px;
`;

const StyledSubLayout = styled(Layout)`
  max-height: 100vh;
  overflow: auto;
  background-color: ${(props) => props.theme.base_color};
  overscroll-behavior-x: none;
  width: 100%;
`;
